'use client';

import { Box, Flex, Grid, Typography } from '@coolblue-development/freddy';
import { freddyTheme } from '@coolblue-development/freddy';
import addItemToBundleTrigger from '@coolblue-development/ga4-events/triggers/Configurator/addItemToBundleTrigger';
import removeItemFromBundleTrigger from '@coolblue-development/ga4-events/triggers/Configurator/removeItemFromBundleTrigger';
import selectEnergyLabelInformationTrigger from '@coolblue-development/ga4-events/triggers/Configurator/selectEnergyLabelInformationTrigger';
import selectItemTrigger from '@coolblue-development/ga4-events/triggers/Configurator/selectItemTrigger';
import showEnergyLabelTrigger from '@coolblue-development/ga4-events/triggers/Configurator/showEnergyLabelTrigger';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import EnergyLabel from '@coolblue-development/next-energy-label/client';
import { MiniProductPage } from '@coolblue-development/next-mini-product-page/client';
import {
  ProductTitle,
  SelectableBox,
  SelectableBoxGroup,
  SelectableBoxProps,
  SquareProductImage,
  WideProductImage
} from '@coolblue-development/next-patterns/client';
import { UsbCChargerIncluded } from '@coolblue-development/next-patterns/client';
import {
  SalesPrice,
  SalesPriceCurrent,
  SalesPriceFormer
} from '@coolblue-development/next-sales-price/client';
import isPropValid from '@emotion/is-prop-valid';
import styled from '@emotion/styled';
import { PersonalPrice } from '@features/product/typings';
import useTestId from '@hooks/useTestId';

import AlternativeOverlayEntrance from './AlternativesOverlay/AlternativeOverlayEntrance';
import { useConfigurator } from './ConfiguratorContext';
import ConfiguratorProductSlotQuantitySelector from './ConfiguratorProductSlotQuantitySelector';
import ConfiguratorProductSlotReview from './ConfiguratorProductSlotReview';
import { Configurator } from './types';

type Props = {
  configurator: Configurator;
};

export default function ConfiguratorProductSlots({ configurator }: Props) {
  const testId = useTestId('configurator-product-slot');
  const descriptionTestId = useTestId('slot-description');
  const descriptionMobileTestId = useTestId('slot-description-mobile');
  const { trackEvent } = useAnalytics();
  const {
    states: { selectedUseCaseIndex, checkboxValues, configuratorValues },
    actions: { updateConfiguratorValue, getProductByUseCaseAndSlot }
  } = useConfigurator();
  const selectedSlots = configurator.useCases[selectedUseCaseIndex].slots;
  const hasProductAlternatives = selectedSlots.some(slot => slot.products.length > 1);
  const numberOfRows = hasProductAlternatives ? 4 : 3;

  const trackChanges = (index: number, isChecked: boolean) => {
    const product = getProductByUseCaseAndSlot({
      useCaseIndex: selectedUseCaseIndex,
      slotIndex: index
    });

    const params = {
      sub_feature: 'configurator' as const,
      index: index,
      total_indices: selectedSlots.length,
      item_id: product?.id || 0
    };

    if (isChecked) {
      trackEvent(removeItemFromBundleTrigger(params));
      return;
    }
    trackEvent(addItemToBundleTrigger(params));
  };

  const handleChange = (value: string) => {
    const isChecked = checkboxValues.includes(value);

    trackChanges(Number(value), isChecked);

    const updatedCheckboxValues = isChecked
      ? checkboxValues.filter(item => item !== value)
      : [...checkboxValues, value];

    const updatedConfiguratorValues = (configuratorValues.get(selectedUseCaseIndex) || []).map(
      (item, index) => ({
        ...item,
        selected: updatedCheckboxValues.includes(String(index))
      })
    );

    updateConfiguratorValue(updatedConfiguratorValues);
  };

  const handleMiniProductPageOpen = (productId: number, index: number, totalIndices: number) => {
    const params = {
      index: index,
      total_indices: totalIndices,
      item_id: productId
    };

    trackEvent(selectItemTrigger(params));
  };

  const onEnergyLabelContentClick = (
    content: 'product_information_sheet' | 'energy_label_image'
  ) => {
    trackEvent(
      selectEnergyLabelInformationTrigger({
        content
      })
    );
  };

  return (
    <SelectableBoxGroup
      type='checkbox'
      value={checkboxValues}
      name='configuratorSlots'
      inputPlacement='floating'
    >
      <Grid
        gap={200}
        sx={{
          [freddyTheme.breakpoint.minWidth('xl')]: {
            ':has(.configurator-slot-item:only-child)': {
              gridTemplateColumns: '1fr'
            }
          }
        }}
        templateColumns={{
          xs: '1fr',
          md: `repeat(${selectedSlots.length}, 1fr)`
        }}
      >
        {selectedSlots.map((slot, index) => {
          const product = getProductByUseCaseAndSlot({
            useCaseIndex: selectedUseCaseIndex,
            slotIndex: index
          });

          const personalPrice = product?.personalPrice?.price.includingVat;
          const salesPrice = product?.salesPrice?.includingVat || 0;
          const price = personalPrice || salesPrice || 0;
          const priceWithDiscount = price - price * (slot.discountPercentage / 100);
          const hasDiscount = slot.discountPercentage > 0;

          if (!product?.id) {
            return null;
          }

          const discountedPersonalPrice: PersonalPrice | undefined = personalPrice
            ? {
                price: {
                  includingVat: priceWithDiscount
                },
                labelText: product?.personalPrice?.labelText || ''
              }
            : undefined;

          const salesPriceComponent = (
            <SalesPrice
              currentPrice={
                <SalesPriceCurrent price={personalPrice ? salesPrice : priceWithDiscount} />
              }
              {...(hasDiscount && {
                formerPrice: <SalesPriceFormer price={salesPrice} />
              })}
              personalPrice={discountedPersonalPrice}
            />
          );

          const slotInformation = (
            <>
              <ProductTitle>
                <ProductTitle.Title mb={100}>
                  <MiniProductPage
                    customSalesPrice={salesPriceComponent}
                    productId={product.id}
                    onOpen={() =>
                      handleMiniProductPageOpen(product.id, index, selectedSlots.length)
                    }
                  >
                    {product.name}
                  </MiniProductPage>
                </ProductTitle.Title>

                <ConfiguratorProductSlotReview
                  product={product}
                  salesPriceComponent={salesPriceComponent}
                />
              </ProductTitle>
              <Typography mb={0} mt={{ md: 100 }} hideFrom='md' {...descriptionMobileTestId}>
                {slot.shortDescription || slot.description || product?.prosAndCons?.pros?.[0]}
              </Typography>
              <Typography mb={0} mt={{ md: 100 }} hideUntil='md' {...descriptionTestId}>
                {slot.description || product?.prosAndCons?.pros?.[0]}
              </Typography>
            </>
          );

          const quantitySelector = slot?.quantitySelector?.isEnabled && (
            <ConfiguratorProductSlotQuantitySelector index={index} />
          );

          const energyLabel = product?.energyLabel && (
            <EnergyLabel
              level={product.energyLabel.level ?? 0}
              size='small'
              direction='right'
              imageId={Number(product?.energyLabel.image?.id)}
              onShow={() => trackEvent(showEnergyLabelTrigger())}
              onContentClick={onEnergyLabelContentClick}
              {...(product?.energyLabel?.specification?.absoluteUrl && {
                specificationUrl: product.energyLabel.specification.absoluteUrl
              })}
            />
          );

          const usbCChargerDetails = product && product.hasUsbCChargerIncluded !== null && (
            <UsbCChargerIncluded hasUsbCChargerIncluded={product.hasUsbCChargerIncluded} />
          );

          return (
            <Box
              className='configurator-slot-item'
              key={`${selectedUseCaseIndex}-${index}`}
              {...testId}
              display='contents'
            >
              <Box hideUntil='md' display='contents'>
                <StyledSelectableBox
                  numberOfRows={numberOfRows}
                  value={String(index)}
                  aria-label={product.name}
                  onClick={() => handleChange(String(index))}
                >
                  <Box
                    display='grid'
                    alignSelf='stretch'
                    sx={{ gap: freddyTheme.spacing[200], textAlign: 'center' }}
                  >
                    <Typography
                      variant='h5'
                      textTransform='capitalize'
                      component='div'
                      sx={{ hyphens: 'auto' }}
                      mb={0}
                      mx={500}
                    >
                      {product.productType?.singularName}
                    </Typography>

                    <Box alignSelf='end'>
                      <WideProductImage
                        alt={product.name || ''}
                        size='small'
                        imageId={product.primaryImage?.id || ''}
                        loading='lazy'
                      />
                    </Box>
                  </Box>

                  <Box display='grid' alignSelf='stretch' sx={{ gap: freddyTheme.spacing[200] }}>
                    <Box>{slotInformation}</Box>

                    {quantitySelector && <Box alignSelf='end'>{quantitySelector}</Box>}
                  </Box>

                  <Box display='grid' alignSelf='stretch' sx={{ gap: freddyTheme.spacing[200] }}>
                    {energyLabel && <Box>{energyLabel}</Box>}
                    {usbCChargerDetails && <Box>{usbCChargerDetails}</Box>}

                    <Box alignSelf='end'>{salesPriceComponent}</Box>
                  </Box>

                  {slot.products.length > 1 && (
                    <Box>
                      <AlternativeOverlayEntrance slotIndex={index} />
                    </Box>
                  )}
                </StyledSelectableBox>
              </Box>

              <Box hideFrom='md' display='contents'>
                <StyledSelectableBox
                  numberOfRows={numberOfRows}
                  value={String(index)}
                  aria-label={product.name}
                  onClick={() => handleChange(String(index))}
                >
                  <Flex gap={300}>
                    <Box mt={600}>
                      <SquareProductImage
                        alt={product.name || ''}
                        size='medium'
                        imageId={product.primaryImage?.id || ''}
                        loading='lazy'
                      />
                    </Box>

                    <Flex direction='column' gap={200}>
                      <Typography
                        component='div'
                        hideFrom='md'
                        variant='h5'
                        textTransform='capitalize'
                        sx={{ hyphens: 'auto' }}
                        mb={0}
                      >
                        {product.productType?.singularName}
                      </Typography>

                      <Flex direction='column' gap={200}>
                        {slotInformation}
                        {quantitySelector}
                        {energyLabel}
                        {usbCChargerDetails}
                      </Flex>

                      {salesPriceComponent}

                      {slot.products.length > 1 && <AlternativeOverlayEntrance slotIndex={index} />}
                    </Flex>
                  </Flex>
                </StyledSelectableBox>
              </Box>
            </Box>
          );
        })}
      </Grid>
    </SelectableBoxGroup>
  );
}

interface StyledSelectableBoxProps extends SelectableBoxProps {
  numberOfRows: number;
}

const StyledSelectableBox = styled(SelectableBox, {
  shouldForwardProp: prop => isPropValid(prop)
})<StyledSelectableBoxProps>`
  label {
    background-color: unset !important;
    border-color: ${({ theme }) => theme.color.background.interactiveControlContrastBase};
    border-width: ${({ theme }) => theme.borderWidth[25]};
    width: 100%;
  }

  label:has(input[type='checkbox']:checked) {
    padding: ${({ theme }) => theme.spacing[400]};
  }

  ${({ theme }) => theme.breakpoint.minWidth('md')} {
    label {
      display: grid;
      grid-template-rows: subgrid;
      grid-row: span ${({ numberOfRows }) => numberOfRows};
    }
  }
`;
